import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "@/lib/api";
import { supabase, isSupabaseEnabled } from "@/lib/supabase";
import { useAuth } from "@/lib/auth/AuthContext";
import type { Session } from "@supabase/supabase-js";

export const Route = createFileRoute("/admin")({
  component: AdminPage,
});

// ─── Back Office Security & Admin Access V1 ────────────────────────────────
// /admin now defers entirely to the app's own Supabase session (useAuth(),
// the same context every other authenticated surface uses) instead of
// hand-rolling a second, duplicate session mechanism. Three things changed
// from the previous "login básico":
//
// 1. No more `localStorage["rhea_admin_token"]` — that was a hand-copied
//    duplicate of the token Supabase's own client already persists (see
//    lib/supabase.ts, `persistSession: true`). A second copy never gets
//    refreshed when Supabase rotates the access token, and never gets
//    invalidated if the Supabase session itself is revoked — it would keep
//    "working" purely because the JWT hadn't expired yet. We still clear
//    the deprecated key defensively (old browsers may still have it) but
//    never write to it again.
// 2. No more insecure fallback when Supabase isn't configured. The old code
//    would, in that case, send whatever the user typed as a password
//    straight to /admin/metrics as a Bearer token and unlock the dashboard
//    if the backend happened to accept it (e.g. RHEA_REQUIRE_AUTH=false, in
//    which case ANY value "worked"). That was purely cosmetic auth by its
//    own on-screen admission. Now: no Supabase config → fail closed, show a
//    clear "configuración incompleta" screen, no login form at all.
// 3. A 403 (authenticated, but not on the backend's RHEA_ADMIN_EMAILS
//    allowlist) now reads as its own honest state — "tu cuenta no tiene
//    permisos de administrador" — never a generic "Error: HTTP 403".

const DEPRECATED_LS_TOKEN_KEY = "rhea_admin_token";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CityCount {
  city_iata: string;
  searches:  number;
}

interface AvailabilityBreakdown {
  available:           number;
  unavailable:         number;
  skipped:             number;
  error:               number;
  not_applicable:      number;
  total_checked:       number;
  confirmed_rate_pct:  number | null;
}

interface MetricsSection {
  total_requests:        number | null;
  unique_cities_seen:    number | null;
  availability_rate_pct: number | null;
  top_cities:            CityCount[];
  availability:          AvailabilityBreakdown;
  errors:                Record<string, number>;
}

// ─── Admin Data Readiness V1 ────────────────────────────────────────────────

interface SupplyCoverage {
  search_requests:               number;
  coverage_tier_breakdown:       Record<string, number>;
  avg_provider_count:            number | null;
  avg_total_results:             number | null;
  zero_provider_destinations:    CityCount[];
  thin_supply_destinations:      CityCount[];
  top_unsupported_destinations:  CityCount[];
  zero_result_searches:          number;
}

interface ProviderFunnel {
  results_loaded: number;
  note:           string;
}

// ─── Admin Metrics UX V1 — Marketplace Operating Dashboard ─────────────────
// "look-to-book proxy" / "provider-click funnel" — NEVER "booking
// conversion". A provider click means the user left Sift Trip to view/book
// with the provider; it is not a confirmed booking. See each section's own
// `available`/`*_note` field — a false or missing value here always means
// "No disponible todavía", never a fabricated 0.

interface MarketplaceFunnelStep {
  event_name: string;
  label:      string;
  count:      number;
}

interface MarketplaceFunnel {
  available:               boolean;
  error:                   string | null;
  steps:                   MarketplaceFunnelStep[];
  dropoff_pct:              (number | null)[];
  look_to_book_proxy_pct:   number | null;
  formula:                  string | null;
}

interface DestinationDemandRow {
  destination_iata:                 string;
  destination_name:                 string | null;
  searches:                         number;
  results_loaded:                   number;
  avg_provider_count:               number | null;
  provider_clicks:                  number | null;
  click_through_rate_pct:           number | null;
  coverage_tier:                    string | null;
  zero_provider_count:              number;
  last_seen:                        string | null;
  flag_high_demand_low_supply:      boolean;
  flag_high_demand_low_click_rate:  boolean;
  flag_zero_provider_supply:        boolean;
}

interface MarketSourceRow {
  provider_name:            string;
  detail_opens:              number;
  provider_clicks:           number;
  click_through_rate_pct:    number | null;
  results_shown:             number | null;
  avg_price:                 number | null;
}

interface TrafficEntry { value: string; count: number; }

interface TrafficSection {
  available:            boolean;
  top_utm_sources:      TrafficEntry[];
  top_utm_mediums:      TrafficEntry[];
  top_utm_campaigns:    TrafficEntry[];
  top_referrer_hosts:   TrafficEntry[];
  top_landing_paths:    TrafficEntry[];
  device_breakdown:     Record<string, number>;
}

interface UsersSection {
  available:                          boolean;
  unique_sessions:                    number | null;
  returning_sessions:                 number | null;
  avg_searches_per_session:           number | null;
  avg_provider_clicks_per_session:    number | null;
  logged_in_vs_anonymous:             null;
  unavailable_note:                   string;
}

interface EngagementSection {
  available:                    boolean;
  duration_bucket_breakdown:    Record<string, number>;
  account_tab_breakdown:        Record<string, number>;
  comparison_usage_rate_pct:    number | null;
  comparison_saved_count:       number;
  comparison_save_rate_pct:     number | null;
  filter_usage_rate_pct:        number | null;
  sort_usage_rate_pct:          number | null;
  unavailable_note:             string;
}

interface MarketplaceErrorsSection {
  available:                       boolean;
  marketplace_panel_error_count:   number;
  intent_parser_fallback_count:    number;
  sequence_error_note:             string;
}

// ─── Sprint: Product Analytics V1 ──────────────────────────────────────────

interface TopFilterRow { filter_type: string; filter_value: string; count: number; }

interface WishlistSection {
  available:                 boolean;
  items_saved:                number;
  items_removed:               number;
  wishlist_usage_rate_pct:     number | null;
}

interface PreferencesSection {
  available:               boolean;
  language_distribution:   Record<string, number>;
  currency_distribution:   Record<string, number>;
  note:                     string;
}

interface AuthFunnelSection {
  available:              boolean;
  auth_started:            number;
  auth_completed:          number;
  auth_success_rate_pct:   number | null;
  auth_method_breakdown:   Record<string, number>;
}

interface PersistentSection extends MetricsSection {
  source:         "supabase" | "unavailable";
  error:          string | null;
  window_days:    number;
  error_rate_pct?: number | null;
  /** Present only when Supabase is reachable — same "unavailable" honesty
   *  rule as the rest of this section, never fabricated when absent. */
  supply_coverage?: SupplyCoverage | null;
  provider_funnel?: ProviderFunnel | null;
  marketplace_funnel:  MarketplaceFunnel;
  destination_demand:  DestinationDemandRow[];
  market_sources:      MarketSourceRow[];
  traffic:              TrafficSection;
  users:                UsersSection;
  engagement:           EngagementSection;
  marketplace_errors:   MarketplaceErrorsSection;
  // Sprint: Product Analytics V1
  top_filters:          TopFilterRow[];
  wishlist:             WishlistSection;
  preferences:          PreferencesSection;
  auth_funnel:          AuthFunnelSection;
}

interface SinceDeploySection extends MetricsSection {
  since: string;
}

// ─── Admin Metrics Clarity Hotfix V1 — source metadata + diagnostics ───────
// Two independent data sources feed /admin/metrics: backend-generated
// api_requests (keyed by city_iata) and frontend-generated marketplace_events
// (keyed by destination_iata). Their counts can legitimately diverge — these
// types/sections exist to make that divergence legible, not to unify them.

interface MetricsSourceMeta {
  source_table: string;
  iata_field:   string;
  description:  string;
  powers:       string[];
}

interface MetricsSources {
  backend_search:  MetricsSourceMeta;
  frontend_events: MetricsSourceMeta;
}

interface RecentEventRow {
  created_at:       string | null;
  event_name:       string | null;
  destination_iata: string | null;
  device_type:      string | null;
  utm_source:       string | null;
  utm_medium:       string | null;
  utm_campaign:     string | null;
  landing_path:     string | null;
}

interface BackendSearchEventGap {
  backend_search_rows:              number | null;
  frontend_search_submitted_events: number | null;
  frontend_results_loaded_events:   number | null;
  note:                              string;
}

interface TestTrafficFilter {
  include_test_traffic: boolean;
  excluded_event_rows:  number | null;
  note:                 string;
  backend_search_note:  string;
}

// Marketplace Search Reliability V1, Part C — debug/audit traffic exclusion
// for api_requests, mirroring TestTrafficFilter's shape/purpose for
// marketplace_events above.
interface DebugTrafficFilter {
  include_debug_traffic:           boolean;
  excluded_row_count:              number | null;
  total_row_count_before_filter:   number | null;
  note:                             string;
}

interface Diagnostics {
  event_counts_by_name:     Record<string, number>;
  recent_events:            RecentEventRow[];
  backend_search_event_gap: BackendSearchEventGap;
  test_traffic_filter:      TestTrafficFilter;
  debug_traffic_filter:     DebugTrafficFilter;
}

interface MetricsResponse {
  success:         boolean;
  provider_mode:   string;
  persistent:      PersistentSection;
  since_deploy:    SinceDeploySection;
  coming_soon:     string[];
  metrics_sources: MetricsSources;
  diagnostics:     Diagnostics;
}

// ─── Fetch outcome — distinguishes "not authorized" from "something broke" ──

type FetchOutcome =
  | { kind: "ok"; data: MetricsResponse }
  | { kind: "unauthorized" }     // 401 — session invalid/expired
  | { kind: "forbidden" }        // 403 — authenticated, not an admin
  | { kind: "error"; status: number | null };

async function fetchAdminMetrics(
  accessToken: string,
  includeTestTraffic: boolean,
  includeDebugTraffic: boolean,
): Promise<FetchOutcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15_000);
  try {
    const params = new URLSearchParams();
    if (includeTestTraffic) params.set("include_test_traffic", "true");
    if (includeDebugTraffic) params.set("include_debug_traffic", "true");
    const qs = params.toString();
    const url = `${API_BASE}/admin/metrics${qs ? `?${qs}` : ""}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal:  controller.signal,
    });
    if (res.status === 401) return { kind: "unauthorized" };
    if (res.status === 403) return { kind: "forbidden" };
    if (!res.ok) return { kind: "error", status: res.status };
    return { kind: "ok", data: await res.json() };
  } catch {
    // Network error / timeout — never surface the raw exception message.
    return { kind: "error", status: null };
  } finally {
    clearTimeout(timer);
  }
}

// ─── Configuration incomplete — fail closed, no insecure fallback ──────────

function ConfigIncomplete() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <p className="text-2xl font-semibold text-foreground">Sift Trip Admin</p>
        <p className="mt-3 text-sm text-muted-foreground">
          Configuración incompleta — falta <code className="font-mono">VITE_SUPABASE_URL</code>{" "}
          y/o <code className="font-mono">VITE_SUPABASE_ANON_KEY</code> en este entorno.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          Por seguridad, el back office no ofrece un modo de acceso alternativo
          sin Supabase configurado. Configurá esas variables en Vercel para habilitar el login.
        </p>
      </div>
    </div>
  );
}

// ─── Login gate — Supabase email/password only, no custom token storage ────

function LoginGate() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState<string | null>(null);
  const [loading,  setLoading]  = useState(false);

  // ─── Admin Password Recovery Flow V1 ───────────────────────────────────────
  // "Olvidé mi contraseña" — separate loading/message state from the login
  // form itself, since the two actions (sign in vs. request a reset email)
  // are independent and can't share one loading/error slot.
  const [resetSending, setResetSending] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetIsError, setResetIsError] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim() || !supabase) return;
    setLoading(true);
    setError(null);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email:    email.trim(),
        password: password.trim(),
      });
      // No manual session storage here — a successful sign-in updates
      // Supabase's own persisted session, and useAuth()'s onAuthStateChange
      // listener (in AuthContext, wrapping the whole app) picks it up and
      // re-renders AdminPage with a real session automatically.
      if (authError) setError("Credenciales incorrectas o cuenta inexistente.");
    } catch {
      setError("No se pudo conectar. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!supabase) return;
    if (!email.trim()) {
      setResetIsError(true);
      setResetMessage("Ingresá tu email arriba primero.");
      return;
    }
    setResetSending(true);
    setResetIsError(false);
    setResetMessage(null);
    try {
      await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      // Same message whether or not the email is actually registered — a
      // reset endpoint that answers differently for known vs. unknown
      // emails leaks which addresses have admin accounts.
      setResetIsError(false);
      setResetMessage("Te enviamos un enlace para restablecer tu contraseña.");
    } catch {
      setResetIsError(true);
      setResetMessage("No pudimos enviar el enlace. Intentá de nuevo.");
    } finally {
      setResetSending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <p className="text-2xl font-semibold text-foreground">Sift Trip Admin</p>
          <p className="mt-1 text-sm text-muted-foreground">Ingresa con tu cuenta de Supabase</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="email"
            autoFocus
            className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-[oklch(0.580_0.220_256)] focus:ring-1 focus:ring-[oklch(0.580_0.220_256)]"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            autoComplete="current-password"
            className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-[oklch(0.580_0.220_256)] focus:ring-1 focus:ring-[oklch(0.580_0.220_256)]"
          />
          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 dark:bg-red-950 dark:text-red-400">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading || !email.trim() || !password.trim()}
            className="w-full rounded-lg bg-[oklch(0.580_0.220_256)] px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {loading ? "Verificando…" : "Entrar"}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetSending}
            className="text-xs text-muted-foreground underline hover:text-foreground disabled:opacity-40"
          >
            {resetSending ? "Enviando…" : "Olvidé mi contraseña"}
          </button>
          {resetMessage && (
            <p
              className={`mt-2 rounded-lg px-3 py-2 text-xs ${
                resetIsError
                  ? "bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400"
                  : "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400"
              }`}
            >
              {resetMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Authenticated but not authorized as admin ──────────────────────────────

function NotAuthorized({ email, onSignOut }: { email: string | null; onSignOut: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <p className="text-2xl font-semibold text-foreground">Acceso restringido</p>
        <p className="mt-3 text-sm text-muted-foreground">
          {email ? <>Tu cuenta (<span className="font-medium">{email}</span>)</> : "Tu cuenta"}{" "}
          está autenticada, pero no tiene permisos de administrador.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Pedile a un administrador que agregue tu email a la lista de acceso.
        </p>
        <button
          onClick={onSignOut}
          className="mt-6 text-xs text-muted-foreground underline hover:text-foreground"
        >
          Salir
        </button>
      </div>
    </div>
  );
}

// ─── Session invalid/expired ─────────────────────────────────────────────────

function SessionExpired({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <p className="text-2xl font-semibold text-foreground">Sesión expirada</p>
        <p className="mt-3 text-sm text-muted-foreground">
          Tu sesión ya no es válida. Iniciá sesión de nuevo.
        </p>
        <button
          onClick={onSignOut}
          className="mt-6 rounded-lg bg-[oklch(0.580_0.220_256)] px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
        >
          Volver a iniciar sesión
        </button>
      </div>
    </div>
  );
}

// ─── Generic fetch error — never the raw exception/stack ────────────────────

function FetchError({ status, onRetry }: { status: number | null; onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <p className="text-sm text-red-500">
          No se pudieron cargar las métricas{status ? ` (HTTP ${status})` : " (backend no respondió)"}.
        </p>
        <button
          onClick={onRetry}
          className="mt-4 rounded-lg border border-border px-4 py-2 text-xs text-muted-foreground hover:border-foreground hover:text-foreground"
        >
          Reintentar
        </button>
      </div>
    </div>
  );
}

// ─── Admin Metrics Clarity Hotfix V1 — source label ────────────────────────
// A quiet badge, not a warning: just states which of the two independent
// systems (backend api_requests vs. frontend marketplace_events) a given
// card/table's numbers come from.

function SourceTag({ source }: { source: "backend" | "frontend" }) {
  return (
    <span className="shrink-0 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
      Fuente: {source === "backend" ? "backend search" : "frontend events"}
    </span>
  );
}

// ─── Metric Card ─────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
  accent,
  source,
}: {
  label:   string;
  value:   string | number | null;
  sub?:    string;
  accent?: boolean;
  source?: "backend" | "frontend";
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        {source && <SourceTag source={source} />}
      </div>
      <p className={`mt-1 text-3xl font-bold ${accent ? "text-[oklch(0.580_0.220_256)]" : "text-foreground"}`}>
        {value ?? "—"}
      </p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ─── Cities bar chart ─────────────────────────────────────────────────────────

function CitiesBar({ cities }: { cities: CityCount[] }) {
  if (!cities.length)
    return <p className="text-xs text-muted-foreground">Sin datos aún.</p>;
  const max = cities[0]?.searches || 1;
  return (
    <div className="space-y-2">
      {cities.map((c, i) => {
        const pct = Math.round((c.searches / max) * 100);
        return (
          <div key={c.city_iata} className="flex items-center gap-3">
            <span className="w-5 text-right text-xs text-muted-foreground">{i + 1}</span>
            <span className="w-12 font-mono text-xs font-medium text-foreground">{c.city_iata}</span>
            <div className="flex-1 overflow-hidden rounded-full bg-muted h-2">
              <div className="h-2 rounded-full bg-[oklch(0.580_0.220_256)]" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 text-right text-xs text-muted-foreground">{c.searches}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Availability breakdown ───────────────────────────────────────────────────

function AvailPanel({ av }: { av: AvailabilityBreakdown }) {
  const total = av.total_checked || 0;
  if (!total) return <p className="text-xs text-muted-foreground">Sin datos. Se registra en búsquedas con fecha.</p>;
  return (
    <div className="grid grid-cols-3 gap-4 sm:grid-cols-5">
      {[
        { label: "Confirmados",   key: "available",      color: "text-green-600" },
        { label: "No disponible", key: "unavailable",    color: "text-red-500"   },
        { label: "Sin fecha",     key: "skipped",        color: "text-amber-500" },
        { label: "N/A",           key: "not_applicable", color: "text-muted-foreground" },
        { label: "Error",         key: "error",          color: "text-red-400"   },
      ].map(({ label, key, color }) => (
        <div key={key} className="text-center">
          <p className={`text-2xl font-bold ${color}`}>{(av as unknown as Record<string, number>)[key] || 0}</p>
          <p className="mt-1 text-xs text-muted-foreground">{label}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Admin Metrics UX V1 — shared small pieces ─────────────────────────────

function UnavailableNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
      No disponible todavía — {children}
    </p>
  );
}

function fmtPct(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${v}%`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-CO", { day: "numeric", month: "short" }); }
  catch { return "—"; }
}

const TABS = [
  { id: "overview",     label: "Overview" },
  { id: "destinations", label: "Destinos" },
  { id: "providers",    label: "Proveedores" },
  { id: "traffic",      label: "Tráfico" },
  { id: "users",        label: "Usuarios" },
  { id: "errors",       label: "Errores" },
  { id: "diagnostics",  label: "Diagnóstico" },
] as const;

type TabId = typeof TABS[number]["id"];

function TabBar({ active, onChange }: { active: TabId; onChange: (t: TabId) => void }) {
  return (
    <div className="mb-6 flex flex-wrap gap-1 rounded-xl border border-border bg-card p-1">
      {TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            active === t.id
              ? "bg-[oklch(0.580_0.220_256)] text-white"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Overview tab (KPIs + funnel) ───────────────────────────────────────────

function OverviewTab({ p }: { p: PersistentSection }) {
  const sc = p.supply_coverage;
  const mf = p.marketplace_funnel;
  const resultsLoaded = mf.steps.find((s) => s.event_name === "marketplace_results_loaded")?.count ?? null;
  const providerClicks = mf.steps.find((s) => s.event_name === "marketplace_provider_clicked")?.count ?? null;

  return (
    <>
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard label="Búsquedas totales" value={sc?.search_requests ?? null} accent source="backend" />
        <MetricCard label="Resultados cargados" value={resultsLoaded} source="frontend" />
        <MetricCard label="Clicks a proveedor" value={mf.available ? providerClicks : null} source="frontend" />
        <MetricCard label="Usuarios únicos" value={p.users.available ? p.users.unique_sessions : null} sub="sesiones anónimas" source="frontend" />
        <MetricCard label="Provider count prom." value={sc?.avg_provider_count ?? null} source="backend" />
        <MetricCard label="Búsquedas sin resultados" value={sc?.zero_result_searches ?? null} source="backend" />
        <MetricCard label="Tasa de error" value={fmtPct(p.error_rate_pct)} source="backend" />
        <MetricCard
          label="Look-to-book proxy"
          value={mf.available ? fmtPct(mf.look_to_book_proxy_pct) : "—"}
          sub="clicks a proveedor / resultados cargados"
          accent
          source="frontend"
        />
      </div>

      <div className="mb-8 rounded-xl border border-border bg-card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Embudo de marketplace (provider-click funnel)</h3>
          <div className="flex items-center gap-2">
            <SourceTag source="frontend" />
            {!mf.available && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-400">No disponible</span>}
          </div>
        </div>
        {mf.formula && <p className="mb-4 text-xs text-muted-foreground">{mf.formula}</p>}
        {!mf.available ? (
          <UnavailableNote>{mf.error ?? "no se pudo leer marketplace_events."}</UnavailableNote>
        ) : (
          <div className="space-y-2">
            {mf.steps.map((step, i) => {
              const max = mf.steps[0]?.count || 1;
              const pct = Math.round((step.count / max) * 100);
              const drop = i > 0 ? mf.dropoff_pct[i - 1] : null;
              return (
                <div key={step.event_name} className="flex items-center gap-3">
                  <span className="w-36 shrink-0 truncate text-xs text-muted-foreground">{step.label}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className="h-2 rounded-full bg-[oklch(0.580_0.220_256)]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 shrink-0 text-right text-xs font-medium text-foreground">{step.count}</span>
                  <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">
                    {drop !== null ? `−${fmtPct(drop)}` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {sc && (
        <div className="mb-8 rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Coverage tier (histórico)</h3>
            <SourceTag source="backend" />
          </div>
          {Object.keys(sc.coverage_tier_breakdown).length === 0 ? (
            <p className="text-xs text-muted-foreground">Sin datos aún.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {Object.entries(sc.coverage_tier_breakdown).map(([tier, count]) => (
                <div key={tier} className="text-center">
                  <p className="text-xl font-bold text-foreground">{count}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{tier.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-8 rounded-xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Availability gate (histórico)</h3>
          <SourceTag source="backend" />
        </div>
        <AvailPanel av={p.availability as AvailabilityBreakdown} />
      </div>
    </>
  );
}

// ─── Destinations tab ────────────────────────────────────────────────────────

function DestinationsTab({ rows }: { rows: DestinationDemandRow[] }) {
  if (rows.length === 0) return <UnavailableNote>sin búsquedas de marketplace en esta ventana.</UnavailableNote>;
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <h3 className="text-sm font-semibold text-foreground">Demanda por destino</h3>
        <SourceTag source="frontend" />
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-3 py-2 font-medium">Destino</th>
            <th className="px-3 py-2 font-medium">Búsquedas</th>
            <th className="px-3 py-2 font-medium">Resultados</th>
            <th className="px-3 py-2 font-medium">Provider count prom.</th>
            <th className="px-3 py-2 font-medium">Clicks proveedor</th>
            <th className="px-3 py-2 font-medium">CTR</th>
            <th className="px-3 py-2 font-medium">Coverage</th>
            <th className="px-3 py-2 font-medium">Últ. visto</th>
            <th className="px-3 py-2 font-medium">Señales</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.destination_iata} className="border-b border-border/50 last:border-0">
              <td className="px-3 py-2 font-medium text-foreground">
                {d.destination_name ?? d.destination_iata} <span className="text-muted-foreground">({d.destination_iata})</span>
              </td>
              <td className="px-3 py-2">{d.searches}</td>
              <td className="px-3 py-2">{d.results_loaded}</td>
              <td className="px-3 py-2">{d.avg_provider_count ?? "—"}</td>
              <td className="px-3 py-2">{d.provider_clicks ?? "—"}</td>
              <td className="px-3 py-2">{fmtPct(d.click_through_rate_pct)}</td>
              <td className="px-3 py-2 text-muted-foreground">{d.coverage_tier?.replace(/_/g, " ") ?? "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{fmtDate(d.last_seen)}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {d.flag_zero_provider_supply && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-700 dark:bg-red-950 dark:text-red-400">sin proveedor</span>
                  )}
                  {d.flag_high_demand_low_supply && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700 dark:bg-amber-950 dark:text-amber-400">demanda alta / supply baja</span>
                  )}
                  {d.flag_high_demand_low_click_rate && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] text-blue-700 dark:bg-blue-950 dark:text-blue-400">demanda alta / CTR bajo</span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Providers / market source tab ──────────────────────────────────────────

function ProvidersTab({ rows }: { rows: MarketSourceRow[] }) {
  if (rows.length === 0) return <UnavailableNote>sin clicks/aperturas de detalle registrados en esta ventana.</UnavailableNote>;
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <h3 className="text-sm font-semibold text-foreground">Fuentes de mercado</h3>
        <SourceTag source="frontend" />
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-3 py-2 font-medium">Proveedor</th>
            <th className="px-3 py-2 font-medium">Detalle abierto</th>
            <th className="px-3 py-2 font-medium">Clicks a proveedor</th>
            <th className="px-3 py-2 font-medium">CTR (clicks / detalle)</th>
            <th className="px-3 py-2 font-medium">Resultados mostrados</th>
            <th className="px-3 py-2 font-medium">Precio prom.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.provider_name} className="border-b border-border/50 last:border-0">
              <td className="px-3 py-2 font-medium text-foreground">{r.provider_name}</td>
              <td className="px-3 py-2">{r.detail_opens}</td>
              <td className="px-3 py-2">{r.provider_clicks}</td>
              <td className="px-3 py-2">{fmtPct(r.click_through_rate_pct)}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.results_shown ?? "No disponible"}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.avg_price ?? "No disponible"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Traffic tab ─────────────────────────────────────────────────────────────

function TrafficList({ title, entries }: { title: string; entries: TrafficEntry[] }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin datos aún.</p>
      ) : (
        <div className="space-y-1.5">
          {entries.map((e) => (
            <div key={e.value} className="flex items-center justify-between text-xs">
              <span className="truncate text-foreground">{e.value}</span>
              <span className="text-muted-foreground">{e.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TrafficTab({ traffic, preferences }: { traffic: TrafficSection; preferences: PreferencesSection }) {
  const languageEntries = Object.entries(preferences.language_distribution).map(([value, count]) => ({ value, count }));
  const currencyEntries = Object.entries(preferences.currency_distribution).map(([value, count]) => ({ value, count }));
  if (!traffic.available) return <UnavailableNote>sin datos de tráfico (utm/referrer/device) en esta ventana.</UnavailableNote>;
  const deviceEntries = Object.entries(traffic.device_breakdown).map(([value, count]) => ({ value, count }));
  return (
    <>
      <div className="mb-4 flex justify-end"><SourceTag source="frontend" /></div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <TrafficList title="UTM source" entries={traffic.top_utm_sources} />
        <TrafficList title="UTM medium" entries={traffic.top_utm_mediums} />
        <TrafficList title="UTM campaign" entries={traffic.top_utm_campaigns} />
        <TrafficList title="Referrer host" entries={traffic.top_referrer_hosts} />
        <TrafficList title="Landing path" entries={traffic.top_landing_paths} />
        <TrafficList title="Dispositivo" entries={deviceEntries} />
      </div>

      {/* Sprint: Product Analytics V1 — cambios explícitos de idioma/moneda,
          no la preferencia actual de cada sesión (ver preferences.note). */}
      <div className="mb-2 mt-6 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Preferencias (cambios explícitos)</span>
        <SourceTag source="frontend" />
      </div>
      {!preferences.available ? (
        <UnavailableNote>sin eventos currency_changed/language_changed en esta ventana.</UnavailableNote>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <TrafficList title="Cambios de idioma" entries={languageEntries} />
            <TrafficList title="Cambios de moneda" entries={currencyEntries} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{preferences.note}</p>
        </>
      )}
    </>
  );
}

// ─── Users tab (+ engagement) ────────────────────────────────────────────────

function UsersTab({
  users, engagement, topFilters, wishlist, authFunnel,
}: {
  users:      UsersSection;
  engagement: EngagementSection;
  // Sprint: Product Analytics V1
  topFilters: TopFilterRow[];
  wishlist:   WishlistSection;
  authFunnel: AuthFunnelSection;
}) {
  const topFilterEntries = topFilters.map(f => ({ value: `${f.filter_type}: ${f.filter_value}`, count: f.count }));
  const authMethodEntries = Object.entries(authFunnel.auth_method_breakdown).map(([value, count]) => ({ value, count }));
  const tabEntries = Object.entries(engagement.account_tab_breakdown).map(([value, count]) => ({ value, count }));
  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Basado en sesiones anónimas (session_id), no en cuentas autenticadas.</p>
        <SourceTag source="frontend" />
      </div>
      {!users.available ? (
        <UnavailableNote>{users.unavailable_note}</UnavailableNote>
      ) : (
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MetricCard label="Sesiones únicas" value={users.unique_sessions} sub="sesiones anónimas" accent />
          <MetricCard label="Sesiones recurrentes" value={users.returning_sessions} sub="sesiones anónimas" />
          <MetricCard label="Búsquedas / sesión" value={users.avg_searches_per_session} />
          <MetricCard label="Clicks proveedor / sesión" value={users.avg_provider_clicks_per_session} />
        </div>
      )}
      <div className="mb-3">
        <UnavailableNote>{users.unavailable_note}</UnavailableNote>
      </div>

      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Engagement</span>
        <SourceTag source="frontend" />
      </div>
      {!engagement.available ? (
        <UnavailableNote>sin eventos marketplace_page_engagement en esta ventana.</UnavailableNote>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <MetricCard label="Uso de comparador" value={fmtPct(engagement.comparison_usage_rate_pct)} />
            {/* Sprint: Product Analytics V1 — antes hardcodeado "No
                disponible"; marketplace_filter_applied ahora también
                persiste server-side. Uso de orden sigue PostHog-only. */}
            <MetricCard label="Uso de filtros" value={fmtPct(engagement.filter_usage_rate_pct)} />
            <MetricCard label="Uso de orden" value="No disponible" />
          </div>
          <div className="mb-3 rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Tiempo en página (bucketed)</h3>
            {Object.keys(engagement.duration_bucket_breakdown).length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin datos aún.</p>
            ) : (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                {Object.entries(engagement.duration_bucket_breakdown).map(([bucket, count]) => (
                  <div key={bucket} className="text-center">
                    <p className="text-lg font-bold text-foreground">{count}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{bucket}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <UnavailableNote>{engagement.unavailable_note}</UnavailableNote>
        </>
      )}

      {/* Sprint: Product Analytics V1 — filtros, wishlist, comparación
          guardada, tabs de /cuenta, auth. Mismo patrón visual que el
          resto de esta pestaña — nunca un dato inventado si available
          es false. */}
      <div className="mb-2 mt-6 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Filtros, wishlist y comparador</span>
        <SourceTag source="frontend" />
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard label="Wishlist guardados" value={wishlist.available ? wishlist.items_saved : null} />
        <MetricCard label="Wishlist quitados" value={wishlist.available ? wishlist.items_removed : null} />
        <MetricCard label="Comparaciones guardadas" value={engagement.available ? engagement.comparison_saved_count : null} />
        <MetricCard label="Tasa de guardado (comparador)" value={fmtPct(engagement.comparison_save_rate_pct)} />
      </div>
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <TrafficList title="Filtros más usados" entries={topFilterEntries} />
        <TrafficList title="Tabs de /cuenta abiertos" entries={tabEntries} />
      </div>

      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Autenticación</span>
        <SourceTag source="frontend" />
      </div>
      {!authFunnel.available ? (
        <UnavailableNote>sin eventos auth_started/auth_completed en esta ventana.</UnavailableNote>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid grid-cols-3 gap-4">
            <MetricCard label="Auth iniciados" value={authFunnel.auth_started} />
            <MetricCard label="Auth completados" value={authFunnel.auth_completed} />
            <MetricCard label="Tasa de éxito" value={fmtPct(authFunnel.auth_success_rate_pct)} />
          </div>
          <TrafficList title="Método de auth" entries={authMethodEntries} />
        </div>
      )}
    </>
  );
}

// ─── Errors tab ──────────────────────────────────────────────────────────────

function ErrorsTab({ p }: { p: PersistentSection }) {
  const me = p.marketplace_errors;
  return (
    <>
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <MetricCard label="Errores backend (search+sequence)" value={fmtPct(p.error_rate_pct)} sub="tasa de error" source="backend" />
        <MetricCard label="Panel errors" value={me.available ? me.marketplace_panel_error_count : null} source="frontend" />
        <MetricCard label="Intent parser fallback" value={me.available ? me.intent_parser_fallback_count : null} source="frontend" />
      </div>

      {Object.keys(p.errors || {}).length > 0 && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-5 dark:border-red-800 dark:bg-red-950">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-red-700 dark:text-red-300">Errores backend por tipo</h3>
            <SourceTag source="backend" />
          </div>
          <div className="space-y-1">
            {Object.entries(p.errors).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="font-mono text-xs text-red-600 dark:text-red-400">{k}</span>
                <span className="text-xs font-bold text-red-700 dark:text-red-300">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <UnavailableNote>{me.sequence_error_note}</UnavailableNote>
    </>
  );
}

// ─── Admin Metrics Clarity Hotfix V1 — source explainer + diagnostics ──────

function SourceExplainer() {
  return (
    <div className="mb-6 rounded-xl border border-border bg-card px-4 py-3 text-xs text-muted-foreground">
      Búsquedas/resultados vienen del backend; funnel/tráfico/usuarios vienen de eventos frontend.
      Pueden no coincidir exactamente.
    </div>
  );
}

function DiagnosticsPanel({ diagnostics }: { diagnostics: Diagnostics }) {
  const gap = diagnostics.backend_search_event_gap;
  const filter = diagnostics.test_traffic_filter;
  const debugFilter = diagnostics.debug_traffic_filter;
  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Backend vs. frontend — conteos</h3>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Backend search rows</span>
            <span className="font-medium text-foreground">{gap.backend_search_rows ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Frontend search submitted</span>
            <span className="font-medium text-foreground">{gap.frontend_search_submitted_events ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Frontend results loaded</span>
            <span className="font-medium text-foreground">{gap.frontend_results_loaded_events ?? "—"}</span>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{gap.note}</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Filtro de tráfico test/smoke</h3>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Incluir tráfico test</span>
            <span className="font-medium text-foreground">{filter.include_test_traffic ? "Sí" : "No"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Eventos excluidos</span>
            <span className="font-medium text-foreground">{filter.excluded_event_rows ?? "—"}</span>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{filter.note}</p>
        <p className="mt-1 text-xs text-muted-foreground">{filter.backend_search_note}</p>
      </div>
      {/* Marketplace Search Reliability V1, Part C — makes explicit that
          operational backend-search volume excludes debug=true (technical
          audit) rows by default, and exactly how many rows that removed. */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Filtro de auditorías (debug)</h3>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Incluir auditorías debug</span>
            <span className="font-medium text-foreground">{debugFilter.include_debug_traffic ? "Sí" : "No"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Filas debug excluidas</span>
            <span className="font-medium text-foreground">{debugFilter.excluded_row_count ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Filas totales antes del filtro</span>
            <span className="font-medium text-foreground">{debugFilter.total_row_count_before_filter ?? "—"}</span>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{debugFilter.note}</p>
      </div>
    </div>
  );
}

function RecentEventsTable({ events }: { events: RecentEventRow[] }) {
  if (events.length === 0) return <UnavailableNote>No hay eventos recientes en esta ventana.</UnavailableNote>;
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-3 py-2 font-medium">Hora</th>
            <th className="px-3 py-2 font-medium">Evento</th>
            <th className="px-3 py-2 font-medium">Destino</th>
            <th className="px-3 py-2 font-medium">Dispositivo</th>
            <th className="px-3 py-2 font-medium">UTM source</th>
            <th className="px-3 py-2 font-medium">Landing path</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={`${e.created_at ?? "row"}-${i}`} className="border-b border-border/50 last:border-0">
              <td className="px-3 py-2 text-muted-foreground">
                {e.created_at ? new Date(e.created_at).toLocaleTimeString("es-CO") : "—"}
              </td>
              <td className="px-3 py-2 font-medium text-foreground">{e.event_name ?? "—"}</td>
              <td className="px-3 py-2">{e.destination_iata ?? "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{e.device_type ?? "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{e.utm_source ?? "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{e.landing_path ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiagnosticsTab({ diagnostics }: { diagnostics: Diagnostics }) {
  return (
    <>
      <DiagnosticsPanel diagnostics={diagnostics} />
      <div className="mb-3 text-sm font-semibold text-foreground">Últimos eventos</div>
      <RecentEventsTable events={diagnostics.recent_events} />
    </>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

function Dashboard({
  session, userEmail, onSignOut, onUnauthorized, onForbidden,
}: {
  session:        Session;
  userEmail:      string | null;
  onSignOut:      () => void;
  onUnauthorized: () => void;
  onForbidden:    () => void;
}) {
  const [data,        setData]        = useState<MetricsResponse | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [fetchStatus,  setFetchStatus] = useState<number | null | "ok">("ok");
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [activeTab,   setActiveTab]   = useState<TabId>("overview");
  const [includeTestTraffic, setIncludeTestTraffic] = useState(false);
  const [includeDebugTraffic, setIncludeDebugTraffic] = useState(false);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    const outcome = await fetchAdminMetrics(session.access_token, includeTestTraffic, includeDebugTraffic);
    if (outcome.kind === "unauthorized") { onUnauthorized(); return; }
    if (outcome.kind === "forbidden")    { onForbidden();    return; }
    if (outcome.kind === "error")        { setFetchStatus(outcome.status); setLoading(false); return; }
    setData(outcome.data);
    setFetchStatus("ok");
    setLastRefresh(new Date());
    setLoading(false);
  }, [session.access_token, includeTestTraffic, includeDebugTraffic, onUnauthorized, onForbidden]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  function handleLogout() {
    onSignOut();
  }

  if (loading && !data)
    return <div className="flex min-h-screen items-center justify-center"><p className="text-sm text-muted-foreground">Cargando métricas…</p></div>;
  if (fetchStatus !== "ok" || !data)
    return <FetchError status={typeof fetchStatus === "number" ? fetchStatus : null} onRetry={fetchMetrics} />;

  const { persistent: p, since_deploy: s, provider_mode, coming_soon, diagnostics } = data;

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto max-w-6xl">

        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Sift Trip Back Office</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Proveedor: <span className="font-medium">{provider_mode}</span> ·
              últimos {p.window_days} días
              {userEmail && <> · <span className="font-medium">{userEmail}</span></>}
              {" · "}
              {p.source === "supabase"
                ? <span className="text-green-600 dark:text-green-400">Supabase ✓</span>
                : <span className="text-amber-600 dark:text-amber-400">Supabase no disponible</span>
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIncludeTestTraffic((v) => !v)}
              className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
            >
              {includeTestTraffic ? "Ocultar tráfico test" : "Incluir tráfico test"}
            </button>
            <button
              onClick={() => setIncludeDebugTraffic((v) => !v)}
              className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
            >
              {includeDebugTraffic ? "Ocultar auditorías debug" : "Incluir auditorías debug"}
            </button>
            <button
              onClick={fetchMetrics}
              disabled={loading}
              className="rounded-lg border border-border px-4 py-2 text-xs text-muted-foreground transition-colors hover:border-foreground hover:text-foreground disabled:opacity-40"
            >
              {loading ? "Actualizando…" : "↻ Actualizar"}
            </button>
            <button
              onClick={handleLogout}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Salir
            </button>
          </div>
        </div>

        {p.error && (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
            DB error: {p.error}
          </p>
        )}

        <SourceExplainer />

        <TabBar active={activeTab} onChange={setActiveTab} />

        {activeTab === "overview"     && <OverviewTab p={p} />}
        {activeTab === "destinations" && <DestinationsTab rows={p.destination_demand} />}
        {activeTab === "providers"    && <ProvidersTab rows={p.market_sources} />}
        {activeTab === "traffic"      && <TrafficTab traffic={p.traffic} preferences={p.preferences} />}
        {activeTab === "users"        && (
          <UsersTab
            users={p.users}
            engagement={p.engagement}
            topFilters={p.top_filters}
            wishlist={p.wishlist}
            authFunnel={p.auth_funnel}
          />
        )}
        {activeTab === "errors"       && <ErrorsTab p={p} />}
        {activeTab === "diagnostics"  && <DiagnosticsTab diagnostics={diagnostics} />}

        {/* ── Low-key footer: since-deploy + roadmap, not primary content ── */}
        <details className="mt-10 rounded-xl border border-border bg-card p-5">
          <summary className="cursor-pointer text-sm font-semibold text-foreground">
            Desde el último deploy y próximamente
          </summary>
          <div className="mt-4">
            <p className="mb-3 text-xs text-muted-foreground">{new Date(s.since).toLocaleString("es-CO")}</p>
            <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <MetricCard label="Requests"      value={s.total_requests}        accent />
              <MetricCard label="Ciudades"      value={s.unique_cities_seen} />
              <MetricCard label="Availability"  value={s.availability_rate_pct !== null ? `${s.availability_rate_pct}%` : null} sub="Esta sesión" />
            </div>
            <CitiesBar cities={s.top_cities} />
            <div className="mt-6 flex flex-wrap gap-2">
              {coming_soon.map((m) => (
                <span key={m} className="rounded-full border border-dashed border-muted-foreground/40 px-3 py-1 text-xs text-muted-foreground">
                  {m.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </div>
        </details>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Última actualización: {lastRefresh.toLocaleTimeString("es-CO")}
        </p>

      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function AdminPage() {
  const { user, session, loading, signOut } = useAuth();
  // Two distinct "logged in, but can't see the dashboard" states, set once
  // fetchAdminMetrics reports them — both cleared on sign-out so switching
  // accounts always re-checks from scratch.
  const [forbidden, setForbidden] = useState(false);
  const [expired,   setExpired]   = useState(false);

  // One-time hygiene cleanup of the deprecated duplicate token — we never
  // write to this key anymore, but an old session may have left it behind.
  useEffect(() => {
    try { localStorage.removeItem(DEPRECATED_LS_TOKEN_KEY); } catch { /* ignore */ }
  }, []);

  const handleSignOut = useCallback(() => {
    setForbidden(false);
    setExpired(false);
    void signOut();
  }, [signOut]);

  // Fail closed: no Supabase configuration → no login form, no fallback.
  if (!isSupabaseEnabled || !supabase) return <ConfigIncomplete />;

  if (loading)
    return <div className="flex min-h-screen items-center justify-center"><p className="text-sm text-muted-foreground">Cargando…</p></div>;

  if (!session) return <LoginGate />;

  if (expired)    return <SessionExpired onSignOut={handleSignOut} />;
  if (forbidden)  return <NotAuthorized email={user?.email ?? null} onSignOut={handleSignOut} />;

  return (
    <Dashboard
      session={session}
      userEmail={user?.email ?? null}
      onSignOut={handleSignOut}
      onUnauthorized={() => setExpired(true)}
      onForbidden={() => setForbidden(true)}
    />
  );
}
